// schema
import * as ISchema from "@schema";

import { logger } from "@util/logger.util";

import { BillingGroupRoleEnum } from "@clockwise/web-commons/src/ui/admin-panel/billing-groups-members/billing-groups-members.type";

export const maybeGetBillingGroupsFromOrg = (org: ISchema.IOrg) => {
  if (org.billingGroups.__typename === "GraphEntityError") {
    logger.error("failed to maybeGetBillingGroupsFromOrg");
    return;
  }

  return org.billingGroups.list;
};

export const getBillingGroupForId = (org: ISchema.IOrg, billingGroupId: string) => {
  const billingGroupsForOrg = maybeGetBillingGroupsFromOrg(org);

  if (!billingGroupsForOrg) {
    return;
  }

  return billingGroupsForOrg.find((bg) => bg.id === billingGroupId);
};

export function getOrgBillingGroup(org: ISchema.IOrg) {
  const billingGroups = maybeGetBillingGroupsFromOrg(org);

  if (!billingGroups) {
    return null;
  }

  const maybeOrgBillingGroup = billingGroups.find((bg) => {
    return bg.syncSources.find((ss) => ss.type === "Org");
  });

  return maybeOrgBillingGroup || null;
}

export const getManualMembersFromBillingGroup = (billingGroup: ISchema.IBillingGroup) => {
  if (billingGroup.manualMembers.__typename === "GraphEntityError") {
    logger.error("failed to getManualMembersFromBillingGroup");
    return;
  }

  return billingGroup.manualMembers.list;
};

export const getAdminsFromBillingGroup = (billingGroup: ISchema.IBillingGroup) => {
  if (billingGroup.adminOrgPersons.__typename === "GraphEntityError") {
    logger.error("failed to getAdminsFromBillingGroup");
    return;
  }

  return billingGroup.adminOrgPersons.list;
};

export const getSubscriptionFromBillingGroup = (billingGroup: ISchema.IBillingGroup) => {
  if (!billingGroup.subscription || billingGroup.subscription.__typename === "GraphEntityError") {
    logger.error("failed to getSubscriptionFromBillingGroup");
    return;
  }

  return billingGroup.subscription;
};

export const getStripeCustomerPortalSessionFromBillingGroup = (
  billingGroup: ISchema.IBillingGroup,
) => {
  if (!billingGroup.stripeCustomerPortalSession) {
    return;
  } else if (billingGroup.stripeCustomerPortalSession.__typename === "GraphEntityError") {
    logger.error("failed to getStripeCustomerPortalSessionFromBillingGroup");
    return;
  }

  return billingGroup.stripeCustomerPortalSession;
};

export const isCurrentUserInBillingGroup = (billingGroup: ISchema.IBillingGroup) => {
  const manualMembers = getManualMembersFromBillingGroup(billingGroup);

  if (!manualMembers) {
    return;
  }

  return (
    manualMembers.findIndex((mm) => mm.orgPerson.__typename === "OrgPerson" && mm.orgPerson.isYou) >
    -1
  );
};

const isUserinBillingGroup = (billingGroup: ISchema.IBillingGroup, userId: string) => {
  const manualMembers = getManualMembersFromBillingGroup(billingGroup);

  if (!manualMembers) {
    return;
  }

  return manualMembers.findIndex((mm) => mm.userId === userId) !== -1;
};

const isPersonInBillingGroup = (billingGroup: ISchema.IBillingGroup, personId: string) => {
  const manualMembers = getManualMembersFromBillingGroup(billingGroup);

  if (!manualMembers) {
    return;
  }

  return (
    manualMembers.findIndex((mm) => {
      const p = (mm.orgPerson.__typename === "OrgPerson" && mm.orgPerson) || null;
      return p && p.personId === personId;
    }) !== -1
  );
};

export const isOrgPersonInBillingGroup = (
  billingGroup: ISchema.IBillingGroup,
  orgPerson: ISchema.IOrgPerson,
) => {
  if (orgPerson.userId) {
    return isUserinBillingGroup(billingGroup, orgPerson.userId);
  } else {
    return isPersonInBillingGroup(billingGroup, orgPerson.personId);
  }
};

export const getUserRoleForBillingGroup = (
  billingGroup: ISchema.IBillingGroup,
  orgPerson: ISchema.IOrgPerson,
) => {
  if (!orgPerson.userId) {
    return BillingGroupRoleEnum.None;
  }

  const adminOrgPersons = getAdminsFromBillingGroup(billingGroup);

  if (!adminOrgPersons) {
    return BillingGroupRoleEnum.Member;
  }

  return adminOrgPersons.findIndex((a) => a.userId === orgPerson.userId) !== -1
    ? BillingGroupRoleEnum.Admin
    : BillingGroupRoleEnum.Member;
};

export function getBillingGroupNameFromId(org: ISchema.IOrg, billingGroupId: string) {
  const billingGroupsForOrg = maybeGetBillingGroupsFromOrg(org);
  if (!billingGroupsForOrg) {
    return;
  }

  const billingGroupFromId = billingGroupsForOrg.find((bg) => bg.id === billingGroupId);

  return billingGroupFromId?.name;
}

export function isBillingGroupTypeSSO(billingGroup: ISchema.IBillingGroup) {
  if (!billingGroup.type) {
    logger.error("failed to call isBillingGroupTypeSSO because incomplete fragment");
    return false;
  }

  return billingGroup.type === ISchema.BillingGroupTypeEnum.SSO;
}

export function getPersonListCountFromBillingGroup(billingGroup: ISchema.IBillingGroup) {
  if (billingGroup.personListPaginatedErrorable.__typename === "GraphEntityError") {
    logger.error("failed to personListCount");
    return;
  }

  return billingGroup.personListPaginatedErrorable.count;
}
